import NotInterestedIcon from "@mui/icons-material/NotInterested";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField
} from "@mui/material";
import * as React from "react";
import * as uuid from "uuid";
import { Helpers } from "../../../../utils/helpers";
import { IPackItem } from "../Items/item";
import { OfferPackEntry } from "./entry";

export interface OfferPackItem {
    id: string;
    title: string;
    subtitle: string;
    button: string;
    packageId: string;
    googleId: string;
    appleId: string;
    samsungId: string;
    huaweiId: string;
    category: string;
    bonusValue: number;

    conditions: OfferPackCondition[];

    expire: number;
    limit: number;
    validatePayment: boolean;
    deleteExpired: boolean;
    items: IPackItem[];

    badgeColor: string;
    isSpecial: boolean;
    prefab: string;
    extraValue: number | string;

    desc: {
        type: string;
        level: number;
    };

    images: {
        icon: string;
        background: string;
    };
}

export interface OfferPackCondition {
    condition: string;
    operator: "==" | "!=" | ">=" | ">" | "<=" | "<" | "BETWEEN";
    value: string;
    secondValue: string;
}

interface OfferPackProps {
    list: OfferPackItem[];
    classes: any;

    onChange(items: OfferPackItem[]);
}

interface OfferPackState {
    groups: {
        [key: string]: OfferPackItem[];
    };

    activeGroup: string;

    modalVisible: boolean;
    groupName: string;
    groupNameError: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{width: "100%"}}
            {...other}
        >
            {value === index && (
                <Box pl={3} pr={3}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export class OfferPacks extends React.Component<OfferPackProps, OfferPackState> {
    list: OfferPackItem[] = [];

    constructor(props: OfferPackProps) {
        super(props);

        this.list = props.list;

        const groups = this.toGroups();

        this.state = {
            groups,
            activeGroup: Object.entries(groups)[0][0],

            modalVisible: false,
            groupName: "",
            groupNameError: false
        };
    }

    toGroups() {
        return this.list.reduce((result, item) => {
            if (!result[item.category]) {
                result[item.category] = [];
            }

            result[item.category].push(item);

            return result;
        }, {});
    }

    addGroup = () => {
        const groupName = this.state.groupName;
        const groups = {...this.state.groups};

        groups[groupName] = [];

        this.setState({
            groups,
            groupName: "",
            groupNameError: false,
            modalVisible: false
        });

        this.add(groupName);
    };

    switchGroup = (_event, key: string) => {
        const groups = this.toGroups();

        this.setState({
            groups,
            activeGroup: key
        });

        window.scrollTo({top: 0, behavior: "smooth"});
    };

    onChangeGroupName = (e) => {
        let error = false;

        if (this.state.groups[e.target.value]) {
            error = true;
        }

        this.setState({
            groupName: e.target.value,
            groupNameError: error
        });
    };

    add = (category: string) => {
        const item = {
            id: uuid.v4(),
            title: "",
            subtitle: "",
            button: "",
            packageId: "",
            googleId: "",
            appleId: "",
            samsungId: "",
            huaweiId: "",
            category,
            bonusValue: 0,

            conditions: [],

            expire: 0,
            limit: 1,
            validatePayment: true,
            deleteExpired: true,
            items: [],

            badgeColor: null,
            isSpecial: false,
            prefab: null,
            extraValue: null,

            desc: {
                type: null,
                level: null
            },

            images: {
                icon: null,
                background: null
            }
        };

        this.list.push(item);

        this.setState(state => {
            state.groups[category].push(item);

            return state;
        }, () => {
            this.props.onChange(this.list);
        });
    };

    onRemove = (groupName: string, item: OfferPackItem) => {
        const group = [...this.state.groups[groupName]];

        const index = group.findIndex(_item => _item.id === item.id);

        group.splice(index, 1);

        this.list.splice(this.list.findIndex(_item => _item.id === item.id), 1);

        this.setState(state => {
            state.groups[groupName] = group;

            return state;
        }, () => {
            this.props.onChange(this.list);
        });
    };

    onUpdate = (item: OfferPackItem) => {
        const index = this.list.findIndex(_item => _item.id === item.id);

        this.list[index] = item;

        this.props.onChange(this.list);
    };

    render() {
        if (!this.props.list.length) {
            return <Paper elevation={0}>
                <div className="no-items-placeholder">
                    <NotInterestedIcon/>
                    <h3>No Offer Packs found</h3>
                </div>
            </Paper>;
        }

        const {classes} = this.props;

        return <Paper elevation={0}>
            <Box pl={3} pr={3} style={{flexGrow: 1, display: "flex"}}>
                <Tabs
                    orientation="vertical"
                    variant="scrollable"
                    value={this.state.activeGroup}
                    onChange={this.switchGroup}
                    className={classes.tabs}
                >
                    {Object.entries(this.state.groups).map(entry =>
                        <Tab label={entry[0]} value={entry[0]} {...Helpers.a11yProps(entry[0])}/>)}
                </Tabs>

                {Object.entries(this.state.groups).map(entry =>
                    <TabPanel value={this.state.activeGroup} index={entry[0]}>
                        <Box>
                            <Box>
                                <Table className="min-table-size">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Package ID</TableCell>
                                            <TableCell>Title</TableCell>
                                            <TableCell>Subtitle</TableCell>
                                            <TableCell>Button</TableCell>
                                            <TableCell>Bonus (%)</TableCell>
                                            <TableCell>Limit</TableCell>
                                            <TableCell>Validate Payment?</TableCell>
                                            <TableCell style={{width: 180}}/>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entry[1].map(item =>
                                            <OfferPackEntry key={item.id} entry={item} classes={classes}
                                                            onRemove={(_item) => this.onRemove(entry[0], _item)}
                                                            onUpdate={(_item) => this.onUpdate(_item)}/>)}
                                    </TableBody>
                                </Table>
                            </Box>

                            <Box p={3}>
                                <Button disableElevation variant={"contained"} color={"primary"}
                                        onClick={() => this.add(entry[0])}>
                                    Add
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel>)}
            </Box>

            <Box p={3}>
                <Button disableElevation variant={"contained"} color={"primary"} onClick={() => this.setState({modalVisible: true})}>
                    Add Group
                </Button>
            </Box>

            <Dialog
                open={this.state.modalVisible}
                onClose={() => this.setState({modalVisible: false})}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle id="draggable-dialog-title">
                    Add Group
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl fullWidth required>
                        <TextField
                            required
                            error={this.state.groupNameError}
                            label="Name"
                            value={this.state.groupName}
                            onChange={this.onChangeGroupName}
                            type="text"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            margin="normal"
                        />
                    </FormControl>
                </DialogContent>

                <DialogActions>
                    <Button disableElevation onClick={this.addGroup} color="primary" variant="contained"
                            disabled={this.state.groupNameError}>
                        Add
                    </Button>

                    <Button disableElevation onClick={() => this.setState({modalVisible: false})} variant="contained">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>;
    }
}
